import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { IRestaurantInfo } from '../../../interfaces/restaurant';
import { distinctUntilChanged } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SpecialsStoreService } from '../../../services/specials/specials-store.service';
import { ISpecial } from '../../../interfaces/special';
import { TranslateService } from '@ngx-translate/core';

type SpecialDateValue = Date | string | null;

type SpecialRangeForm = {
  start: FormControl<SpecialDateValue>;
  end: FormControl<SpecialDateValue>;
};

type SpecialForm = {
  range: FormGroup<SpecialRangeForm>;
  name: FormControl<string>;
  specialFile: FormControl<File | null>;
  restaurants: FormControl<number[] | null>;
  toAll: FormControl<boolean>;
};

@UntilDestroy()
@Component({
  selector: 'app-add-special',
  templateUrl: './add-special.component.html',
  styleUrls: ['./add-special.component.scss']
})
export class AddSpecialComponent {

  isLoading = false;
  dateNaw = new Date();

  form: FormGroup<SpecialForm>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      chainId: number,
      allRestaurants: IRestaurantInfo[],
      mode?: 'create' | 'copy',
      source?: ISpecial
    },
    private specialsStore: SpecialsStoreService,
    private translate: TranslateService,
  ) {
    const source = this.data.source;
    const copySuffix = this.translate.instant('pages.specials.modal.copySuffix');
    const attachedToAll = source?.attachedToAll ?? true;
    const attachedRestaurantIds = source && !attachedToAll
      ? source.attachedRestaurantIds ?? []
      : null;

    this.form = new FormGroup<SpecialForm>({
      range: new FormGroup<SpecialRangeForm>({
        start: new FormControl<SpecialDateValue>(source ? new Date(source.scheduleStart) : null, Validators.required),
        end: new FormControl<SpecialDateValue>(source ? new Date(source.scheduleEnd) : null, Validators.required),
      }),
      name: new FormControl(source ? `${source.name} (${copySuffix})` : '', {
        nonNullable: true,
        validators: [Validators.required, Validators.minLength(3)]
      }),
      specialFile: new FormControl<File | null>(null, [Validators.required]),
      restaurants: new FormControl<number[] | null>({
        value: attachedRestaurantIds,
        disabled: attachedToAll
      }),
      toAll: new FormControl(attachedToAll, { nonNullable: true }),
    });

    this.form.controls.toAll.valueChanges.pipe(
      distinctUntilChanged(),
      untilDestroyed(this)
    ).subscribe((value: boolean) => {
      if (value) {
        this.form.controls.restaurants.reset({
          value: [],
          disabled: true,
        });
      } else {
        this.form.controls.restaurants.reset({
          value: null,
          disabled: false,
        });
      }
    });
  }

  get isCopyMode(): boolean {
    return this.data.mode === 'copy';
  }

  public onSubmit(): void {
    if (this.form.invalid || this.isLoading) {
      return;
    }

    this.isLoading = true;
    const formValue = this.form.getRawValue();

    if (!formValue.range.start || !formValue.range.end || !formValue.specialFile) {
      this.isLoading = false;
      return;
    }

    this.specialsStore.addSpecial(
      this.data.chainId,
      formValue.name,
      new Date(formValue.range.start).toISOString(),
      this.specialsStore.adjustEndDate(formValue.range.end),
      formValue.specialFile,
      formValue.toAll ? [] : formValue.restaurants
    ).pipe(
      untilDestroyed(this)
    ).subscribe(
      () => (this.isLoading = false),
      () => (this.isLoading = false)
    );
  }
}
